import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Fingerprint } from 'lucide-react';
import {
  isNative,
  isBiometryAvailable,
  isBiometricLockEnabled,
  setBiometricLockEnabled,
  runBiometricAuth,
} from '../../utils/biometric';

/**
 * Toggle del bloqueo biométrico en el perfil. Solo aparece en la app nativa y si
 * el dispositivo tiene biometría configurada. Activar exige una autenticación de
 * confirmación para no dejar al usuario bloqueado si la biometría no responde.
 */
export const BiometricLockSection: React.FC = () => {
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(isBiometricLockEnabled());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNative()) return;
      const ok = await isBiometryAvailable();
      if (!cancelled) setAvailable(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isNative() || !available) return null;

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    if (!enabled) {
      // Confirmar con biometría antes de activar
      const ok = await runBiometricAuth('Confirma para activar el bloqueo');
      if (ok) {
        setBiometricLockEnabled(true);
        setEnabled(true);
      }
    } else {
      setBiometricLockEnabled(false);
      setEnabled(false);
    }
    setBusy(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-6"
    >
      <h3 className="text-sm font-medium text-[var(--color-text-muted)] mb-3 px-1">
        Seguridad
      </h3>

      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-accent-soft)] flex-shrink-0">
            <Fingerprint size={20} className="text-[var(--color-accent)]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-[var(--color-text)]">Bloqueo biométrico</p>
            <p className="text-sm text-[var(--color-text-muted)]">
              Pide Face ID, Touch ID o huella al abrir la app.
            </p>
          </div>
          <button
            role="switch"
            aria-checked={enabled}
            aria-label="Bloqueo biométrico"
            onClick={toggle}
            disabled={busy}
            className={`relative w-12 h-7 rounded-full flex-shrink-0 transition-colors disabled:opacity-60 ${
              enabled ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border)]'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
